import { ControlConnector } from "@multiweb/reactConnectors/ControlConnector";
import { IAttrValue } from "@multiweb/types/contracts";
import { ReactValueComponent } from "@multiweb/types/react";
import { AttrValueReader } from "OW.Remoting/Remoting/AttrValueReader";
import { FormControlParams, IHTMLElementWithControl } from "UI/UI/UI/Controls/ControlInterfaces";
import { extendElementWithTscControl } from "UI/UI/UI/Controls/Registrator";
import { ItemTemplate } from "../ItemTemplate";

/**
 * Vrací novou instanci {@link ConstructorFunction}, která instanciuje `connector` s definovanou React komponentou.
 * Tuto komponentu v rámci konektoru defaultně nastavuje jako nekompozitní.
 *
 * @param connector Třída, jejíž instance bude vrácená funkce vytvářet.
 * @param component React komponenta, která je skrze `connector` propojena s původní aplikací.
 */
export class ReactComponentTemplate<Component extends ReactValueComponent, Connector extends typeof ControlConnector> extends ItemTemplate {
    private readonly _reactComponent: Component;
    private readonly _connector: Connector;

    constructor(reactComponent: Component, connector: Connector) {
        super();
        this._reactComponent = reactComponent;
        this._connector = connector;
    }

    private _withConnector = (connector: Connector, component: ReactValueComponent) => (
        (params: FormControlParams) => {
            const { element: rootElement, originalRightsDefinition } = params;
            return new connector({
                componentId: rootElement.getAttribute("data-componentId") || undefined,
                component,
                isCompositeComponent: false,
                rootElement,
                originalRightsDefinition,
            });
        }
    );
    public override async updateData(dataSet: IAttrValue[], item: HTMLElement): Promise<void> {
        const data = AttrValueReader.read(this._getDataFromSet(dataSet));

        if (data && item instanceof IHTMLElementWithControl && item.tscControl instanceof ControlConnector)
            item.tscControl.controlValue = data;
    }

    protected override _createContentElement(): HTMLElement {
        const innerElement = document.createElement("div");
        innerElement.setAttribute("data-attrname", this.parentColumn.attrName);
        const controlParams: FormControlParams = {
            ownerForm: undefined,
            element: innerElement,
            originalRightsDefinition: this.parentColumn.originalRightsDefinition,
        };
        const controlOrPromise = this._withConnector(this._connector, this._reactComponent)(controlParams);
        extendElementWithTscControl(innerElement, controlOrPromise);
        return innerElement;
    }
}
